import {
  GamePackAddPromptMutation,
  GamePackCreateMutation,
  GamePackDeleteMutation,
  GamePackGetQuery,
  GamePackIcon,
  GamePackListQuery,
  GamePackPalette,
  GamePackRemovePromptMutation,
  GamePackUpdateMutation,
  PromptCreateMutation,
  PromptDeleteMutation,
  PromptListQuery,
  PromptUpdateMutation
} from '@/API';
import {
  gamePackAddPrompt,
  gamePackCreate,
  gamePackDelete,
  gamePackRemovePrompt,
  gamePackUpdate,
  gamePackUpdateOrder,
  promptCreate,
  promptDelete,
  promptUpdate,
  gamePackOrganizationsCreate,
  gamePackOrganizationsDelete
} from '@/graphql/mutations';
import {
  gamePackGet,
  gamePackList,
  promptList,
  gamePackOrganizationsByGamePackId
} from '@/graphql/queries';
import { generateClient, GraphQLQuery } from 'aws-amplify/api';

const client = generateClient();

// Get All Game Packs
export const listGamePacks = async () => {
  const gamepacks = await client.graphql<GraphQLQuery<GamePackListQuery>>({
    query: gamePackList
  });

  return gamepacks.data?.gamePackList ?? [];
};

// Get Game Packs
export const getGamePack = async (id: string) => {
  return client.graphql<GraphQLQuery<GamePackGetQuery>>({
    query: gamePackGet,
    variables: {
      id
    }
  });
};

export const createGamePack = async ({
  name,
  description,
  icon,
  palette,
  status,
  showInDiscover,
  isNew,
  organizationIds,
  category1,
  category2,
  category3
}: {
  name: string;
  description: string;
  icon: GamePackIcon;
  palette: GamePackPalette;
  status?: string;
  showInDiscover?: boolean;
  isNew?: boolean;
  organizationIds?: string[];
  category1?: string;
  category2?: string;
  category3?: string;
}) => {
  return client.graphql<GraphQLQuery<GamePackCreateMutation>>({
    query: gamePackCreate,
    variables: {
      name,
      description,
      icon,
      palette,
      status,
      showInDiscover,
      isNew,
      organizationIds,
      category1,
      category2,
      category3
    }
  });
};

export const updateGamePack = async ({
  id,
  name,
  icon,
  palette,
  description,
  status,
  showInDiscover,
  isNew,
  organizationIds,
  category1,
  category2,
  category3
}: {
  id: string;
  name: string;
  icon: GamePackIcon;
  palette: GamePackPalette;
  description: string;
  status?: string;
  showInDiscover?: boolean;
  isNew?: boolean;
  organizationIds?: string[];
  category1?: string;
  category2?: string;
  category3?: string;
}) => {
  return client.graphql<GraphQLQuery<GamePackUpdateMutation>>({
    query: gamePackUpdate,
    variables: {
      id,
      name,
      icon,
      palette,
      description,
      status,
      showInDiscover,
      isNew,
      organizationIds,
      category1,
      category2,
      category3
    }
  });
};

export const updateGamePackOrder = async (orders: Array<{ id: string; order: number }>) => {
  return client.graphql({
    query: gamePackUpdateOrder,
    variables: {
      orders
    }
  });
};

export const removeGamePack = async (id: string) => {
  return client.graphql<GraphQLQuery<GamePackDeleteMutation>>({
    query: gamePackDelete,
    variables: { id }
  });
};

export const listPrompts = async () => {
  const result = await client.graphql<GraphQLQuery<PromptListQuery>>({
    query: promptList
  });

  return result.data.promptList ?? [];
};

export const getAllPromptsFromGamePacks = async (tenantId?: string) => {
  const gamepacks = await listGamePacks();

  let filteredGamePacks = gamepacks;
  // Only filter by tenant if tenantId is provided and there are organizations to filter by
  if (tenantId) {
    const gamePacksWithOrgs = gamepacks.filter(
      (gamepack) => (gamepack.organizations ?? []).length > 0
    );
    if (gamePacksWithOrgs.length > 0) {
      filteredGamePacks = gamePacksWithOrgs.filter((gamepack) =>
        (gamepack.organizations ?? []).some((org) => org.tenant === tenantId)
      );
    }
    // If no GamePacks have organizations, or no GamePacks match the tenant filter,
    // return all prompts from all GamePacks (fallback behavior)
  }

  const allPrompts = filteredGamePacks.flatMap((gamepack) => gamepack.prompts);

  const uniquePrompts = allPrompts.filter(
    (prompt, index, self) => index === self.findIndex((p) => p?.id === prompt?.id)
  );

  return uniquePrompts;
};

export const createPrompt = async (text: string) => {
  return client.graphql<GraphQLQuery<PromptCreateMutation>>({
    query: promptCreate,
    variables: {
      text
    }
  });
};

export const removePrompt = async (id: string) => {
  return client.graphql<GraphQLQuery<PromptDeleteMutation>>({
    query: promptDelete,
    variables: { id }
  });
};

export const updatePrompt = async (id: string, text: string) => {
  return client.graphql<GraphQLQuery<PromptUpdateMutation>>({
    query: promptUpdate,
    variables: {
      id,
      text
    }
  });
};

export const addPromptToGamePack = async (gamePackId: string, promptId: string) => {
  return client.graphql<GraphQLQuery<GamePackAddPromptMutation>>({
    query: gamePackAddPrompt,
    variables: {
      id: gamePackId,
      promptId
    }
  });
};

export const removePromptFromGamePack = async (gamePackId: string, promptId: string) => {
  return client.graphql<GraphQLQuery<GamePackRemovePromptMutation>>({
    query: gamePackRemovePrompt,
    variables: { id: gamePackId, promptId }
  });
};

// GamePack-Organization association functions
export const createGamePackOrganization = async (gamePackId: string, organizationId: string) => {
  return client.graphql({
    query: gamePackOrganizationsCreate,
    variables: {
      input: {
        gamePackId,
        organizationId
      }
    }
  });
};

export const deleteGamePackOrganization = async (associationId: string) => {
  return client.graphql({
    query: gamePackOrganizationsDelete,
    variables: { id: associationId }
  });
};

export const getGamePackOrganizations = async (gamePackId: string) => {
  return client.graphql({
    query: gamePackOrganizationsByGamePackId,
    variables: { gamePackId }
  });
};

// export const createNewGamePackOrganizationAssociation = (
//   gamePackId: string,
//   organizationId: string
// ) => {
//   return client.graphql<GraphQLQuery<CreateGamePackOrganizationsMutation>>({
//     query: createGamePackOrganizations,
//     variables: {
//       input: {
//         gamePackId,
//         organizationId
//       }
//     }
//   });
// };

// export const deleteGamePackOrganizationAssociation = (gamePackOrganizationId: string) => {
//   return client.graphql<GraphQLQuery<DeleteGamePackOrganizationsMutation>>({
//     query: deleteGamePackOrganizations,
//     variables: {
//       input: {
//         id: gamePackOrganizationId
//       }
//     }
//   });
// };

// export const createNewGamePack = (
//   gamePack: Partial<GamePack>,
//   groups: string[],
//   orgs: Partial<Organization>[]
// ) => {
//   return client
//     .graphql<GraphQLQuery<CreateGamePackMutation>>({
//       query: createGamePack,
//       variables: {
//         input: {
//           name: gamePack.name,
//           description: gamePack.description,
//           status: GamePackStatus.active,
//           groups: groups,
//           isFeatured: false // TODO: not yet implemented in UI
//         }
//       }
//     })
//     .then(async (result) => {
//       const gamePackId = result.data.createGamePack?.id;
//       if (gamePackId && orgs && orgs.length > 0) {
//         const associationPromises = orgs.map(
//           (org) => org.id && createNewGamePackOrganizationAssociation(gamePackId, org.id)
//         );
//         await Promise.all(associationPromises);
//       }
//       return result;
//     });
// };

// export const updateExistingGamePack = (
//   gamePack: Partial<GamePack>,
//   groups: string[],
//   /** Associations with organizations to create */
//   orgsToCreate?: Partial<Organization>[],
//   /** Existing associations with organizations to remove, requires only the association ID */
//   gamePackOrganizationAssociationsToRemove?: (string | undefined)[]
// ) => {
//   if (!gamePack.id) {
//     throw new Error('GamePack id not provide');
//   }

//   const updatePromise = client.graphql<GraphQLQuery<UpdateGamePackMutation>>({
//     query: updateGamePack,
//     variables: {
//       input: {
//         id: gamePack.id,
//         name: gamePack.name,
//         description: gamePack.description,
//         status: gamePack.status,
//         groups: groups,
//         isFeatured: false // TODO: not yet implemented in UI
//       }
//     }
//   });

//   const createGamePackOrgPromises =
//     orgsToCreate?.map((org) => createNewGamePackOrganizationAssociation(gamePack.id!, org.id!)) ??
//     [];

//   const removeGamePackOrgPromises =
//     gamePackOrganizationAssociationsToRemove?.map((id) =>
//       id ? deleteGamePackOrganizationAssociation(id) : new Promise<void>((resolve) => resolve())
//     ) ?? [];

//   return Promise.all([updatePromise, ...createGamePackOrgPromises, ...removeGamePackOrgPromises]);
// };
